import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { BASE_URL } from "../../../config";
import { getAuthHeaders } from "../../../services/useAuth";
import { FaSearch } from "react-icons/fa";
import CustomButton from "../../../pages/CustomButton";
const CosmaciaProductWiseScheme = () => {
  const navigate = useNavigate();

  // dropdown data
  const [states, setStates] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [campaigns, setCampaigns] = useState([]); // optional; will stay empty if no API

  // filters
  const [selectedState, setSelectedState] = useState("");
  const [selectedDivision, setSelectedDivision] = useState("All");
  const [selectedEmployee, setSelectedEmployee] = useState("All");
  const [selectedCampaign, setSelectedCampaign] = useState("-1");

  const now = new Date();
  const [selectedYear, setSelectedYear] = useState(String(now.getFullYear()));
  const [selectedMonth, setSelectedMonth] = useState(String(now.getMonth() + 1).padStart(2, "0"));

  const [schemeOn, setSchemeOn] = useState("0");

  // table / ui
  const [tableData, setTableData] = useState([]);
  const [showContainers, setShowContainers] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasInitialLoad, setHasInitialLoad] = useState(false);

  // search
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const inputRef = useRef(null);
  const handleExpandSearch = () => {
    setIsSearchExpanded(true);
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  // static month/year options
  const months = [
    { name: "Jan", value: "01" },
    { name: "Feb", value: "02" },
    { name: "Mar", value: "03" },
    { name: "Apr", value: "04" },
    { name: "May", value: "05" },
    { name: "Jun", value: "06" },
    { name: "Jul", value: "07" },
    { name: "Aug", value: "08" },
    { name: "Sep", value: "09" },
    { name: "Oct", value: "10" },
    { name: "Nov", value: "11" },
    { name: "Dec", value: "12" },
  ];
  const years = Array.from({ length: 6 }, (_, i) => String(now.getFullYear() - i)); // current → -5

  // employees fetch
  const fetchEmployees = useCallback(async (stateId, divisionName) => {
    if (!stateId) return;
    try {
      let url = `${BASE_URL}/api/employees?state_id=${stateId}`;
      if (divisionName && divisionName !== "All") {
        url += `&division=${encodeURIComponent(divisionName)}`;
      }
      const res = await axios.get(url, { headers: getAuthHeaders() });
      const list = Array.isArray(res.data) ? res.data : res.data?.data || [];
      setEmployees([{ id: "All", first_name: "All", last_name: "" }, ...list]);
      setSelectedEmployee("All");
    } catch (err) {
      console.error("Error fetching employees:", err);
      setEmployees([]);
      setSelectedEmployee("All");
      setError("Failed to load employees");
    }
  }, []);

  // restore from localStorage
  useEffect(() => {
    const saved = localStorage.getItem("pobDataState");
    if (!saved) return;
    try {
      const s = JSON.parse(saved);
      setSelectedState(s.selectedState || "");
      setSelectedDivision(s.selectedDivision ?? "All");
      setSelectedEmployee(s.selectedEmployee ?? "All");
      setSelectedMonth(s.selectedMonth || String(now.getMonth() + 1).padStart(2, "0"));
      setSelectedYear(s.selectedYear || String(now.getFullYear()));
      setSelectedCampaign(s.selectedCampaign ?? "-1");
      setSchemeOn(s.schemeOn ?? "0");
      setTableData(s.tableData || []);
      setShowContainers(Boolean(s.tableData?.length));
      setHasInitialLoad(Boolean(s.tableData?.length));

      if (s.selectedState) {
        axios
          .get(`${BASE_URL}/api/divisions?state_id=${s.selectedState}`, { headers: getAuthHeaders() })
          .then((r) => setDivisions([{ id: "All", name: "All" }, ...(r.data || [])]))
          .catch((e) => {
            console.error("Error restoring divisions:", e);
            setError("Failed to restore divisions");
          });

        const divisionName =
          s.selectedDivision === "All"
            ? "All"
            : (s.divisions || []).find((d) => String(d.id) === String(s.selectedDivision))?.name || "All";
        fetchEmployees(s.selectedState, divisionName);
      }
    } catch (e) {
      console.error("Error restoring state:", e);
    } finally {
      localStorage.removeItem("pobDataState");
    }
  }, [fetchEmployees]);

  // fetch states
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${BASE_URL}/api/states`, { headers: getAuthHeaders() });
        setStates(r.data || []);
      } catch (e) {
        console.error("Error fetching states:", e);
        setError("Failed to load states");
      }
    })();
  }, []);

  // optionally fetch campaigns (ignore silently if 404)
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${BASE_URL}/api/campaigns`, { headers: getAuthHeaders() });
        const list = Array.isArray(r.data) ? r.data : r.data?.data || [];
        setCampaigns(list);
      } catch {
        setCampaigns([]); // keep empty if endpoint not present
      }
    })();
  }, []);

  // divisions by state
  useEffect(() => {
    if (!selectedState) {
      setDivisions([]);
      setSelectedDivision("All");
      return;
    }
    (async () => {
      try {
        const r = await axios.get(`${BASE_URL}/api/divisions?state_id=${selectedState}`, {
          headers: getAuthHeaders(),
        });
        setDivisions([{ id: "All", name: "All" }, ...(r.data || [])]);
        setSelectedDivision("All");
      } catch (e) {
        console.error("Error fetching divisions:", e);
        setError("Failed to load divisions");
      }
    })();
  }, [selectedState]);

  const handleStateChange = async (stateId) => {
    setSelectedState(stateId);
    setSelectedDivision("All");
    setSelectedEmployee("All");
    setError(null);
    if (stateId) {
      try {
        const r = await axios.get(`${BASE_URL}/api/divisions?state_id=${stateId}`, {
          headers: getAuthHeaders(),
        });
        setDivisions([{ id: "All", name: "All" }, ...(r.data || [])]);
      } catch (e) {
        console.error("Error loading divisions", e);
        setError("Failed to load divisions");
      }
      fetchEmployees(stateId, "All");
    } else {
      setDivisions([]);
      setEmployees([]);
    }
  };

  const handleDivisionChange = async (divisionId) => {
    setSelectedDivision(divisionId);
    setSelectedEmployee("All");
    setError(null);
    const division = divisions.find((d) => String(d.id) === String(divisionId));
    const divisionName = division?.name || "All";
    fetchEmployees(selectedState, divisionName);
  };

  const handleNumberInput = (e) => {
    setSchemeOn(e.target.value);
  };

  const handleGoClick = useCallback(async () => {
    if (!selectedState) {
      setError("Please select a state first");
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      let url = `${BASE_URL}/api/summary?state_id=${selectedState}&month=${selectedMonth}&year=${selectedYear}`;
      if (selectedDivision && selectedDivision !== "All") {
        const division = divisions.find((d) => String(d.id) === String(selectedDivision));
        if (division?.name) url += `&division=${encodeURIComponent(division.name)}`;
      }
      if (selectedEmployee && selectedEmployee !== "All") {
        url += `&employee_id=${selectedEmployee}`;
      }
      if (selectedCampaign && selectedCampaign !== "-1") {
        url += `&campaign_id=${selectedCampaign}`;
      }
      if (schemeOn !== "") {
        url += `&scheme_on=${encodeURIComponent(String(schemeOn))}`;
      }
      if (searchQuery) {
        url += `&search=${encodeURIComponent(searchQuery)}`;
      }

      const r = await axios.get(url, { headers: getAuthHeaders() });
      const payload = Array.isArray(r.data) ? r.data : r.data?.data || [];
      setTableData(payload);
      setShowContainers(true);
      setHasInitialLoad(true);
    } catch (e) {
      console.error("Failed to fetch summary", e);
      if (e.response) {
        setError(`Server error: ${e.response.status}. ${e.response.data?.message || "Please try again."}`);
      } else if (e.request) {
        setError("Network error. Please check your connection and try again.");
      } else {
        setError("Something went wrong while fetching data. Please try again.");
      }
      setTableData([]);
    } finally {
      setIsLoading(false);
    }
  }, [
    selectedState,
    selectedDivision,
    selectedEmployee,
    selectedMonth,
    selectedYear,
    selectedCampaign,
    schemeOn,
    searchQuery,
    divisions,
  ]);

  // debounced search
  useEffect(() => {
    if (showContainers && hasInitialLoad) {
      const t = setTimeout(() => handleGoClick(), 500);
      return () => clearTimeout(t);
    }
  }, [searchQuery, showContainers, hasInitialLoad, handleGoClick]);

  const handleNavigation = (path, employeeId) => {
    const filterState = {
      selectedState,
      selectedDivision,
      selectedEmployee,
      selectedMonth,
      selectedYear,
      selectedCampaign,
      schemeOn,
      tableData,
      divisions,
      employees,
    };
    navigate(`${path}?month=${selectedMonth}&year=${selectedYear}`);
    localStorage.setItem("pobDataState", JSON.stringify(filterState));
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    handleGoClick();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setShowContainers(true);
    handleGoClick();
  };

  const handleRetry = () => {
    setError(null);
    handleGoClick();
  };

  return (
    <div className="pob-data-main-container">


     {/* Filter Section */}
<div className="bg-white rounded-xl shadow-md p-4 mb-4">
  <fieldset className="border border-gray-300 p-4 rounded-lg">
    <form onSubmit={handleSubmit}>
     <h2 className="text-2xl font-bold flex justify-center gap-2 mb-6">
    <span className="text-blue-700">ProductWise </span>
    <span className="text-blue-500">Sale</span>
  </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
        {/* State */}
        <div className="flex items-center">
          <label
            htmlFor="state"
            className="w-1/4 text-sm font-medium text-gray-700"
          >
            State
          </label>
          <div className="w-3/4 flex justify-start">
            <select
              id="state"
              value={selectedState}
              onChange={(e) => handleStateChange(e.target.value)}
              className="w-full max-w-xs px-3 py-1.5 border border-gray-300 rounded-md text-sm "
            >
              <option value="">Select</option>
              {states.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Year */}
        <div className="flex items-center">
          <label
            htmlFor="year"
            className="w-1/4 text-sm font-medium text-gray-700"
          >
            Year
          </label>
          <div className="w-3/4 flex justify-start">
            <select
              id="year"
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              className="w-28 px-3 py-1.5 border border-gray-300 rounded-md text-sm "
            >
              {years.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Month */}
        <div className="flex items-center">
          <label
            htmlFor="month"
            className="w-1/4 text-sm font-medium text-gray-700"
          >
            Month
          </label>
          <div className="w-3/4 flex justify-start">
            <select
              id="month"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="w-28 px-3 py-1.5 border border-gray-300 rounded-md text-sm "
            >
              {months.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Campaign (optional) */}
        <div className="flex items-center">
          <label
            htmlFor="campaign"
            className="w-1/4 text-sm font-medium text-gray-700"
          >
            Campaign
          </label>
          <div className="w-3/4 flex justify-start">
            <select
              id="campaign"
              value={selectedCampaign}
              onChange={(e) => setSelectedCampaign(e.target.value)}
              className="w-full max-w-xs px-3 py-1.5 border border-gray-300 rounded-md text-sm "
            >
              <option value="-1">Select</option>
              {campaigns.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Scheme On */}
        <div className="flex items-center">
          <label
            htmlFor="schemeOn"
            className="w-1/4 text-sm font-medium text-gray-700"
          >
            Scheme On
          </label>
          <div className="w-3/4 flex justify-start">
            <input
              type="number"
              id="schemeOn"
              value={schemeOn}
              onChange={handleNumberInput}
              step="any"
              className="w-24 px-3 py-1.5 border border-gray-300 rounded-md text-sm text-right "
            />
          </div>
        </div>

        {/* Submit */}
        <div className="flex items-center md:col-span-2 justify-center mt-3">
          <CustomButton
            type="submit"
            disabled={isLoading}
            variant="go"
            className="px-6 py-1.5 text-sm"
          >
            {isLoading ? "Loading..." : "GO"}
          </CustomButton>
        </div>
      </div>
    </form>
  </fieldset>
</div>


      {showContainers && (
        <div className="pob-data-table-container">
          <div className="pob-data-title3-container">
            <h2 className="pob-data-title3 text-center">
              POB Data - (
              {selectedState &&
                `State: ${states.find((s) => String(s.id) === String(selectedState))?.name || "All States"}`}
              {" | Division: "}
              {selectedDivision === "All"
                ? "All Divisions"
                : divisions.find((d) => String(d.id) === String(selectedDivision))?.name || "Unknown Division"}
              {" | Employee: "}
              {selectedEmployee === "All"
                ? "All Employees"
                : (() => {
                    const emp = employees.find((e) => String(e.id) === String(selectedEmployee));
                    return emp ? `${emp.first_name} ${emp.last_name}` : "Unknown Employee";
                  })()}
              )
            </h2>

            <div className="relative mb-4 flex justify-end">
              <form onSubmit={handleSearchSubmit} className="flex items-center gap-2">
                <input
                  ref={inputRef}
                  type="text"
                  placeholder="Search employees..."
                  className={`transition-all duration-300 ease-in-out border border-gray-300 rounded-full px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    isSearchExpanded || searchQuery ? "w-64 opacity-100" : "w-0 opacity-0 px-0 py-0"
                  }`}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onBlur={() => !searchQuery && setIsSearchExpanded(false)}
                />
                <button type="button" onClick={handleExpandSearch} className="p-2 text-gray-500 hover:text-gray-700">
                  <FaSearch className="w-5 h-5" />
                </button>
              </form>
            </div>
          </div>

          <div className="pob-data-table-wrapper">
            {isLoading ? (
              <div className="flex justify-center items-center py-8">
                <div className="text-lg text-gray-600">Loading data...</div>
              </div>
            ) : (
              <table className="pob-data-table">
                <thead className="text-white">
                  <tr>
                    <th>S.No</th>
                    <th>EMP ID</th>
                    <th>Employee</th>
                    <th>State</th>
                    <th>HQ</th>
                    <th>Division</th>
                    <th>Designation</th>
                    <th>DOJ</th>
                    <th>Manager</th>
                    <th>No Of Doctors</th>
                    <th>No Of Retailers</th>
                    <th>No Of Stockist</th>
                    <th>No Of Route</th>
                    <th>POB Total</th>
                    <th>POB DateWise</th>
                    <th>Stockist Wise</th>
                    <th>
                      Cumulative <br /> Stockist Wise
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {tableData.length > 0 ? (
                    tableData.map((row, index) => (
                      <tr key={`${row.employee_id}-${index}`}>
                        <td>{index + 1}</td>
                        <td>{row.employee_id}</td>
                        <td>{row.employee_name}</td>
                        <td>{row.state}</td>
                        <td>{row.hq}</td>
                        <td>{row.division}</td>
                        <td>{row.designation}</td>
                        <td>{row.doj}</td>
                        <td>{row.manager}</td>
                        <td
                          className="pob-data-clickable"
                          onClick={() => handleNavigation(`/doctordetails/${row.employee_id}/doctor`)}
                        >
                          {row.doctor_count}
                        </td>
                        <td
                          className="pob-data-clickable"
                          onClick={() => handleNavigation(`/chemistdetails/${row.employee_id}`)}
                        >
                          {row.retailer_count}
                        </td>
                        <td
                          className="pob-data-clickable"
                          onClick={() => handleNavigation(`/stockistdetails/${row.employee_id}`)}
                        >
                          {row.stockist_count}
                        </td>
                        <td>{row.route_count}</td>
                        <td
                          className="pob-data-clickable"
                          onClick={() => handleNavigation(`/pob-details-doctor-chemist/${row.employee_id}`)}
                        >
                          {row.pob_total}
                        </td>
                        <td
                          className="pob-data-clickable"
                          onClick={() => handleNavigation(`/pob-details-doctor-chemist-datewise/${row.employee_id}`)}
                        >
                          {row.pob_total}
                        </td>
                        <td
                          className="pob-data-clickable"
                          onClick={() => handleNavigation(`/pob-details-doctor-chemist-stockistwise/${row.employee_id}`)}
                        >
                          {row.pob_total}
                        </td>
                        <td
                          className="pob-data-clickable"
                          onClick={() => handleNavigation(`/pob-details-stockist/${row.employee_id}`)}
                        >
                          {row.pob_total}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="17" className="pob-data-no-results">
                        No data found for the selected filters
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </div>

          {error && (
            <div className="flex justify-center mt-3">
              <button
                onClick={handleRetry}
                className="px-4 py-2 text-sm bg-gray-200 rounded hover:bg-gray-300"
              >
                Retry
              </button>
            </div>
          )}
        </div>
      )}


    </div>
  );
};

export default CosmaciaProductWiseScheme;
